import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Stethoscope, User, Shield, MapPin, Award,
  ArrowRight, Clock, AlertCircle, Loader2, Star, KeyRound
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";

const PRACTICE_TYPES = [
  { id: "solo", name: "Solo Practice" },
  { id: "group", name: "Group Practice" },
  { id: "hospital_affiliated", name: "Hospital Affiliated" },
  { id: "academic", name: "Academic Medical Center" },
  { id: "fqhc", name: "Federally Qualified Health Center" },
  { id: "urgent_care", name: "Urgent Care" },
  { id: "nursing_home", name: "Nursing Home Medical Director" },
  { id: "other", name: "Other" }
];

const SPECIALTIES = [
  "Internal Medicine", "Family Medicine", "Geriatrics", "Oncology",
  "Pulmonology", "Cardiology", "Nephrology", "Neurology", "Palliative Medicine",
  "Hospitalist", "General Practice", "Other"
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

export default function PhysicianAuth() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("login");
  const [isLoading, setIsLoading] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  const [formData, setFormData] = useState({
    practice_name: "",
    practice_type: "",
    npi_number: "",
    tax_id: "",
    specialty: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
    fax: "",
    physician_name: "",
    physician_npi: "",
    physician_email: "",
    physician_phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    interested_in_champion: true
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await api.post("/physician/login", {
        email: loginEmail,
        password: loginPassword
      });
      
      if (response.data.success) {
        localStorage.setItem("physician_token", response.data.token);
        localStorage.setItem("physician_data", JSON.stringify(response.data.physician));
        toast.success("Login successful!");
        navigate("/physician/dashboard");
      } else {
        toast.error(response.data.message);
        if (response.data.status === "pending") {
          setRegistrationStatus("pending");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { confirmPassword, ...registrationData } = formData;
      const response = await api.post("/physician/register", registrationData);
      
      if (response.data.success) {
        setRegistrationStatus("submitted");
        toast.success("Registration submitted!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  if (registrationStatus === "submitted") {
    return (
      <div className="container mx-auto px-4 py-16 max-w-2xl">
        <Card className="text-center">
          <CardContent className="pt-12 pb-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <Clock className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-4">{t("physician.pending.title")}</h2>
            <p className="text-muted-foreground mb-6">
              {t("physician.pending.message")}
            </p>
            <Alert className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t("physician.pending.notice")}
              </AlertDescription>
            </Alert>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate("/")}>
                {t("common.returnHome")}
              </Button>
              <Button onClick={() => {
                setRegistrationStatus(null);
                setActiveTab("login");
              }}>
                {t("physician.goToLogin")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <Badge className="mb-4" variant="secondary">
          <Award className="w-3 h-3 mr-1" /> {t("physician.portal.championProgram")}
        </Badge>
        <h1 className="text-4xl font-bold mb-4">{t("physician.portal.title")}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          {t("physician.portal.subtitle")}
        </p>
      </div>

      {/* Champion Benefits */}
      <div className="grid md:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto">
        <div className="text-center p-4 bg-muted/50 rounded-lg">
          <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
          <p className="font-medium">{t("physician.benefits.recognition")}</p>
          <p className="text-xs text-muted-foreground">{t("physician.benefits.recognitionDesc")}</p>
        </div>
        <div className="text-center p-4 bg-muted/50 rounded-lg">
          <Stethoscope className="w-8 h-8 mx-auto mb-2 text-primary" />
          <p className="font-medium">{t("physician.benefits.insights")}</p>
          <p className="text-xs text-muted-foreground">{t("physician.benefits.insightsDesc")}</p>
        </div>
        <div className="text-center p-4 bg-muted/50 rounded-lg">
          <ArrowRight className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <p className="font-medium">{t("physician.benefits.referrals")}</p>
          <p className="text-xs text-muted-foreground">{t("physician.benefits.referralsDesc")}</p>
        </div>
        <div className="text-center p-4 bg-muted/50 rounded-lg">
          <Shield className="w-8 h-8 mx-auto mb-2 text-blue-500" />
          <p className="font-medium">{t("physician.benefits.support")}</p>
          <p className="text-xs text-muted-foreground">{t("physician.benefits.supportDesc")}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="login" data-testid="physician-login-tab">
              {t("physician.signIn")}
            </TabsTrigger>
            <TabsTrigger value="register" data-testid="physician-register-tab">
              {t("physician.register")}
            </TabsTrigger>
          </TabsList>

          {/* Login Tab */}
          <TabsContent value="login">
            <Card>
              <CardHeader>
                <CardTitle>{t("physician.signIn.title")}</CardTitle>
                <CardDescription>
                  {t("physician.signIn.description")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {registrationStatus === "pending" && (
                  <Alert className="mb-6">
                    <Clock className="h-4 w-4" />
                    <AlertDescription>
                      {t("physician.pending.pendingApproval")}
                    </AlertDescription>
                  </Alert>
                )}
                
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t("physician.email")}</Label>
                    <Input
                      type="email"
                      placeholder="doctor@practice.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      data-testid="physician-login-email"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t("physician.password")}</Label>
                      <Button 
                        type="button" 
                        variant="link" 
                        className="px-0 h-auto text-xs text-primary"
                        onClick={() => setForgotPasswordOpen(true)}
                        data-testid="physician-forgot-password"
                      >
                        <KeyRound className="w-3 h-3 mr-1" />
                        {t("physician.forgotPassword")}
                      </Button>
                    </div>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      data-testid="physician-login-password"
                    />
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={isLoading} data-testid="physician-login-submit">
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("physician.signingIn")}</>
                    ) : (
                      <>{t("physician.signIn")} <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
            
            {/* Forgot Password Dialog */}
            <ForgotPasswordDialog 
              open={forgotPasswordOpen} 
              onOpenChange={setForgotPasswordOpen}
              userType="physician"
            />
          </TabsContent>

          {/* Registration Tab */}
          <TabsContent value="register">
            <Card>
              <CardHeader>
                <CardTitle>{t("physician.register.title")}</CardTitle>
                <CardDescription>
                  {t("physician.register.description")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-8">
                  {/* Practice Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                      <Stethoscope className="w-5 h-5 text-primary" />
                      {t("physician.practice.title")}
                    </div>
                    
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>{t("physician.practice.name")} *</Label>
                        <Input
                          value={formData.practice_name}
                          onChange={(e) => handleInputChange("practice_name", e.target.value)}
                          placeholder="Smith Medical Group"
                          required
                          data-testid="practice-name-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.practice.type")} *</Label>
                        <Select value={formData.practice_type} onValueChange={(v) => handleInputChange("practice_type", v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {PRACTICE_TYPES.map(type => (
                              <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.practice.specialty")} *</Label>
                        <Select value={formData.specialty} onValueChange={(v) => handleInputChange("specialty", v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select specialty" />
                          </SelectTrigger>
                          <SelectContent>
                            {SPECIALTIES.map(spec => (
                              <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.practice.npi")} *</Label>
                        <Input
                          value={formData.npi_number}
                          onChange={(e) => handleInputChange("npi_number", e.target.value)}
                          placeholder="1234567890"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                      <MapPin className="w-5 h-5 text-primary" />
                      {t("physician.address.title")}
                    </div>
                    
                    <div className="space-y-2">
                      <Label>{t("physician.address.street")} *</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => handleInputChange("address", e.target.value)}
                        placeholder="123 Medical Plaza"
                        required
                      />
                    </div>
                    
                    <div className="grid gap-4 md:grid-cols-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t("physician.address.city")} *</Label>
                        <Input
                          value={formData.city}
                          onChange={(e) => handleInputChange("city", e.target.value)}
                          placeholder="Falls Church"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.address.state")} *</Label>
                        <Select value={formData.state} onValueChange={(v) => handleInputChange("state", v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="State" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map(state => (
                              <SelectItem key={state} value={state}>{state}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.address.zip")} *</Label>
                        <Input
                          value={formData.zip_code}
                          onChange={(e) => handleInputChange("zip_code", e.target.value)}
                          placeholder="22041"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>{t("physician.address.phone")} *</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          placeholder="(703) 555-0100"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.address.fax")}</Label>
                        <Input
                          value={formData.fax}
                          onChange={(e) => handleInputChange("fax", e.target.value)}
                          placeholder="(703) 555-0101"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Physician Info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                      <User className="w-5 h-5 text-primary" />
                      {t("physician.info.title")}
                    </div>
                    
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>{t("physician.info.name")} *</Label>
                        <Input
                          value={formData.physician_name}
                          onChange={(e) => handleInputChange("physician_name", e.target.value)}
                          placeholder="Dr. Jane Smith"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.info.npi")} *</Label>
                        <Input
                          value={formData.physician_npi}
                          onChange={(e) => handleInputChange("physician_npi", e.target.value)}
                          placeholder="1234567890"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.info.email")} *</Label>
                        <Input
                          type="email"
                          value={formData.physician_email}
                          onChange={(e) => handleInputChange("physician_email", e.target.value)}
                          placeholder="dr.smith@practice.com"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.info.phone")} *</Label>
                        <Input
                          value={formData.physician_phone}
                          onChange={(e) => handleInputChange("physician_phone", e.target.value)}
                          placeholder="(703) 555-0102"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Account Credentials */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                      <Shield className="w-5 h-5 text-primary" />
                      {t("physician.account.title")}
                    </div>
                    
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2 md:col-span-2">
                        <Label>{t("physician.account.email")} *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          placeholder="login@practice.com"
                          required
                          data-testid="physician-email-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.account.password")} *</Label>
                        <Input
                          type="password"
                          value={formData.password}
                          onChange={(e) => handleInputChange("password", e.target.value)}
                          placeholder="••••••••"
                          required
                          minLength={8}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t("physician.account.confirm")} *</Label>
                        <Input
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                          placeholder="••••••••"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Champion Program */}
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="champion"
                        checked={formData.interested_in_champion}
                        onCheckedChange={(v) => handleInputChange("interested_in_champion", v)}
                      />
                      <div>
                        <Label htmlFor="champion" className="font-semibold">
                          <Award className="w-4 h-4 inline mr-1 text-yellow-600" />
                          {t("physician.champion.enroll")}
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t("physician.champion.enrollDesc")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" size="lg" disabled={isLoading} data-testid="physician-register-submit">
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("physician.submitting")}</>
                    ) : (
                      <>{t("physician.register")} <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
